import { Link, router, useForm, usePage } from '@inertiajs/react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { ErrorSummary, type ErrorSummaryItem } from '@/components/public/forms/ErrorSummary';
import FormActions from '@/components/public/forms/FormActions';
import RequiredFieldsNote from '@/components/public/forms/RequiredFieldsNote';
import { SubmissionReceipt } from '@/components/public/forms/SubmissionReceipt';
import Select from '@/components/public/ui/Select';
import { trackUmamiEvent } from '@/lib/analytics';
import { inputClass, inputErrorClass } from '@/lib/formStyles';

type FlashProps = { flash: { notice?: string; alert?: string } };

const FIELD_LABELS: Record<string, string> = {
  name: 'Nome completo',
  email: 'Endereço de correio eletrónico',
  subject: 'Assunto',
  message: 'Mensagem',
  consent: 'Consentimento',
};

const FIELD_IDS: Record<string, string> = {
  name: 'contact-name',
  email: 'contact-email',
  subject: 'contact-subject',
  message: 'contact-message',
  consent: 'contact-consent',
};

export default function ContactForm() {
  const { flash } = usePage<FlashProps>().props;
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const form = useForm({
    name: '',
    email: '',
    subject: '',
    message: '',
    consent: false,
    consented_at: '',
  });
  const { data, setData, processing, errors, reset } = form;

  const e = errors as unknown as Record<string, string | undefined>;
  const visibleErrors = { ...e };
  if (attemptedSubmit && !data.consent) {
    visibleErrors.consent = 'é obrigatório';
  } else if (data.consent) {
    delete visibleErrors.consent;
  }

  const errorItems: ErrorSummaryItem[] = Object.entries(visibleErrors)
    .filter((entry): entry is [string, string] => Boolean(entry[1]))
    .map(([key, message]) => ({
      key,
      label: FIELD_LABELS[key] ?? key,
      message,
      href: FIELD_IDS[key] ? `#${FIELD_IDS[key]}` : undefined,
    }));

  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    setAttemptedSubmit(true);
    if (!data.consent) {
      trackUmamiEvent('form_submit_blocked', { form_type: 'contact', error_count: 1 });
      return;
    }
    form.transform((d) => ({ message: { ...d, consented_at: new Date().toISOString() } }));
    trackUmamiEvent('contact_submit', { subject: data.subject || 'unselected' });
    form.post('/contact');
  };

  if (flash?.notice) {
    return (
      <SubmissionReceipt
        formType="contact"
        title="Mensagem recebida pela Junta"
        message={flash.notice}
        steps={[
          { label: 'Registo', description: 'A mensagem foi registada com data e consentimento.' },
          { label: 'Triagem', description: 'Os serviços analisam o assunto indicado.' },
          {
            label: 'Resposta',
            description: 'Os serviços entrarão em contacto se forem necessários dados adicionais.',
          },
        ]}
        actions={[
          {
            label: 'Enviar outra mensagem',
            onClick: () => {
              reset();
              router.get('/contact', {}, { replace: true, preserveScroll: true });
            },
          },
          { label: 'Voltar ao início', href: '/', variant: 'primary' },
        ]}
      />
    );
  }

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-6">
      <RequiredFieldsNote />

      <ErrorSummary items={errorItems} />

      {/* Nome + correio eletrónico */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <label
            className="text-on-surface mb-1.5 block text-sm font-medium"
            htmlFor="contact-name"
          >
            Nome completo
          </label>
          <input
            id="contact-name"
            type="text"
            value={data.name}
            onChange={(ev) => setData('name', ev.target.value)}
            placeholder="Ex: Maria Silva"
            className={e.name ? inputErrorClass : inputClass}
            required
          />
          {e.name && <p className="text-error mt-1 text-xs">{e.name}</p>}
        </div>

        <div>
          <label
            className="text-on-surface mb-1.5 block text-sm font-medium"
            htmlFor="contact-email"
          >
            Endereço de correio eletrónico
          </label>
          <input
            id="contact-email"
            type="email"
            value={data.email}
            onChange={(ev) => setData('email', ev.target.value)}
            placeholder="email@exemplo.pt"
            className={e.email ? inputErrorClass : inputClass}
            required
          />
          {e.email && <p className="text-error mt-1 text-xs">{e.email}</p>}
        </div>
      </div>

      {/* Subject */}
      <div>
        <label
          className="text-on-surface mb-1.5 block text-sm font-medium"
          htmlFor="contact-subject"
        >
          Assunto
        </label>
        <Select
          id="contact-subject"
          value={data.subject}
          onChange={(ev) => setData('subject', ev.target.value)}
          required
          aria-invalid={!!e.subject}
          aria-describedby={e.subject ? 'contact-subject-error' : undefined}
        >
          <option value="">Selecione um assunto</option>
          <option value="geral">Geral</option>
          <option value="servicos">Serviços</option>
          <option value="documentacao">Documentação</option>
          <option value="reclamacao">Reclamação</option>
          <option value="outro">Outro</option>
        </Select>
        {e.subject && (
          <p id="contact-subject-error" className="text-error mt-1 text-xs">
            {e.subject}
          </p>
        )}
      </div>

      {/* Message */}
      <div>
        <label
          className="text-on-surface mb-1.5 block text-sm font-medium"
          htmlFor="contact-message"
        >
          Mensagem
        </label>
        <textarea
          id="contact-message"
          value={data.message}
          onChange={(ev) => setData('message', ev.target.value)}
          placeholder="Escreva aqui a sua mensagem..."
          rows={6}
          className={`${e.message ? inputErrorClass : inputClass} resize-none`}
          required
        />
        {e.message && <p className="text-error mt-1 text-xs">{e.message}</p>}
      </div>

      {/* Consent */}
      <div
        id="contact-consent"
        className={[
          'rounded-xl border p-4',
          attemptedSubmit && !data.consent
            ? 'border-error/30 bg-error/5'
            : 'bg-primary/[0.04] border-primary/15',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={data.consent}
            onChange={(ev) => {
              setData('consent', ev.target.checked);
              if (ev.target.checked) setAttemptedSubmit(false);
            }}
            aria-invalid={attemptedSubmit && !data.consent}
            aria-describedby={
              attemptedSubmit && !data.consent ? 'contact-consent-error' : undefined
            }
            className="mt-1 rounded"
          />
          <span className="text-on-surface-light text-sm leading-relaxed">
            Aceito o tratamento dos meus dados pessoais para resposta a este contacto, nos termos do
            RGPD. Consulte a nossa{' '}
            <Link href="/privacy" className="text-primary underline">
              política de privacidade
            </Link>
            .
          </span>
        </label>
        {visibleErrors.consent && !data.consent && (
          <p id="contact-consent-error" className="text-error mt-2 ml-7 text-xs font-medium">
            Esta confirmação é obrigatória para enviar a mensagem.
          </p>
        )}
      </div>

      <FormActions submitLabel="Enviar mensagem" processing={processing} onReset={() => reset()} />
    </form>
  );
}
